import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import makeStyles from '@material-ui/core/styles/makeStyles'
import InputAdornment from '@material-ui/core/InputAdornment'
import TextField from '@material-ui/core/TextField'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import Button from '@material-ui/core/Button'
import FormHelperText from '@material-ui/core/FormHelperText'
import LocalOfferOutlinedIcon from '@material-ui/icons/LocalOfferOutlined'
import AddIcon from '@material-ui/icons/Add'
import CloseOutlinedIcon from '@material-ui/icons/CloseOutlined'
import Tooltip from '@material-ui/core/Tooltip'
import Alert from '@material-ui/lab/Alert'
import GcgCircularProgress from '../../Loaders/GcgCircularProgress'
import isEmpty from 'lodash/isEmpty'
import { getErrorMessage, inputHasError } from '../../../utils/formErrorUtils'
import { useLocation } from 'react-router-dom'

const useStyles = makeStyles((theme) => ({
    showPromoCodeInputContainer: {
        display: 'flex',
        flex: 1,
        margin: '0 auto 1.3rem auto',
    },
    showPromoCodeInputButton: {
        textTransform: 'none',
        color: theme.palette.primary.main,
    },
    promoCodeContainer: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        justifyContent: 'space-between',
        paddingBottom: '3.5rem',
    },
    promoCodeInputContainer: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
    },
    promoCodeInput: {
        background: '#FFFFFF',
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            '& fieldset': {
                borderWidth: '2px',
                borderColor: '#4F4F4F',
                borderRadius: '8px',
            },
            '&:hover fieldset': {
                borderWidth: '2px',
                borderColor: '#4F4F4F',
            },
            '&.Mui-focused fieldset': {
                borderWidth: '2px',
                borderColor: '#4F4F4F',
            },
        },
    },
    promoCodeInputHelperText: (location) => {
        const isConsumerCheckoutPage = location.pathname === '/cart/'

        return {
            backgroundColor: isConsumerCheckoutPage ? '#F1F1F1' : '#FFFFFF',
            padding: isConsumerCheckoutPage ? '3px 14px 0 14px' : 0,
            margin: isConsumerCheckoutPage ? 0 : null,
        }
    },
    appliedPromoCodeContainer: {
        marginTop: 3,
        display: 'flex',
        flexDirection: 'column',
        width: 'fit-content',
    },
    appliedPromoCodeChip: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#4E4E4E',
        height: '40px',
        borderRadius: '20px',
    },
    saleIcon: {
        marginLeft: '1.1rem',
        color: 'white',
    },
    appliedPromoChipText: {
        margin: 'auto 0rem auto 1rem',
        color: 'white',
    },
    closeIcon: {
        marginRight: '0.5rem',
        color: 'white',
    },
    appliedPromoTextContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: '0.5rem',
    },
    appliedPromoText: {
        fontSize: '12px',
        marginLeft: '0.5rem',
    },
    applyPromoButton: {
        textTransform: 'none',
        color: theme.palette.primary.main,
    },
    toolTipText: {
        fontSize: '1rem',
    },
    promoCodeAlertMessage: {
        marginTop: '1rem',
    },
    whiteRoundTextinput: {
        backgroundColor: 'white',
        height: 57,
        borderRadius: '60px',
        '& fieldset': {
            borderRadius: '60px',
        },
        '& .MuiOutlinedInput-root': {
            borderRadius: '60px',
            '& fieldset': {
                borderRadius: '60px',
            },
            '&:hover fieldset': {
                borderRadius: '60px',
            },
            '&.Mui-focused fieldset': {
                borderRadius: '60px',
            },
        },
    },
}))

const PromoCodeInput = ({
    appliedPromoCode,
    appliedPromoCodeTerms,
    hasQualifiedItems,
    applyPromoIsLoading,
    removePromoIsLoading,
    errors,
    handleApplyPromoCode,
    handleRemovePromoCode,
    roundedInput = false
}) => {
    const location = useLocation()
    const classes = useStyles(location)
    const [showPromoCodeInput, setShowPromoCodeInput] = useState(false)
    const [promoCode, setPromoCode] = useState('')

    // Clear out the promo code input after a promo code has been applied
    useEffect(() => {
        if (!isEmpty(appliedPromoCode)) {
            setPromoCode('')
        }
    }, [appliedPromoCode])

    return (
        <>
            {!isEmpty(appliedPromoCode) ? (
                <>
                    <Box className={classes.promoCodeContainer}>
                        <Typography style={{ fontSize: 14 }}>
                            Promo code
                        </Typography>
                        <Box className={classes.appliedPromoCodeContainer}>
                            <Tooltip
                                title={
                                    <span className={classes.toolTipText}>
                                        {appliedPromoCodeTerms}
                                    </span>
                                }
                                aria-label={appliedPromoCodeTerms}
                                disableFocusListener={!appliedPromoCodeTerms}
                                disableHoverListener={!appliedPromoCodeTerms}
                                disableTouchListener={!appliedPromoCodeTerms}
                            >
                                <Box
                                    data-testid="applied-promo-code-chip"
                                    className={classes.appliedPromoCodeChip}
                                >
                                    <LocalOfferOutlinedIcon
                                        className={classes.saleIcon}
                                        fontSize="small"
                                    />
                                    <Typography
                                        data-testid="applied-promo-code-chip-text"
                                        className={classes.appliedPromoChipText}
                                    >
                                        {appliedPromoCode}
                                    </Typography>
                                    <IconButton
                                        data-testid="remove-promo-code-button"
                                        disabled={removePromoIsLoading}
                                        onClick={handleRemovePromoCode}
                                    >
                                        <CloseOutlinedIcon
                                            className={classes.closeIcon}
                                            fontSize="small"
                                        />
                                    </IconButton>
                                </Box>
                            </Tooltip>
                            <Box className={classes.appliedPromoTextContainer}>
                                <Typography
                                    data-testid="promo-code-applied-text"
                                    className={classes.appliedPromoText}
                                >
                                    {appliedPromoCode} has been applied!
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                    {inputHasError(errors, 'removePromoCode') && (
                        <Box
                            display={'flex'}
                            justifyContent={'center'}
                            marginBottom={'3rem'}
                        >
                            <FormHelperText
                                error={inputHasError(errors, 'removePromoCode')}
                            >
                                {getErrorMessage(errors, 'removePromoCode')}
                            </FormHelperText>
                        </Box>
                    )}
                    {!hasQualifiedItems && (
                        <Box>
                            <Alert
                                severity="warning"
                                className={classes.promoCodeAlertMessage}
                            >
                                The promotional code you entered has no effect.
                                There are no qualified items in your cart.
                            </Alert>
                        </Box>
                    )}
                </>
            ) : !showPromoCodeInput ? (
                <Box className={classes.showPromoCodeInputContainer}>
                    <Button
                        data-testid="show-promo-code-input-button"
                        className={classes.showPromoCodeInputButton}
                        onClick={() => {
                            setShowPromoCodeInput(true)
                        }}
                    >
                        Apply a promo code +
                    </Button>
                </Box>
            ) : (
                <>
                    <Box className={classes.promoCodeInputContainer}>
                        <TextField
                            id="promo-code-input"
                            className={classes.promoCodeInput + (roundedInput ? ` ${classes.whiteRoundTextinput}` : '')}
                            data-testid="promo-code-input"
                            variant="outlined"
                            label="Promo code"
                            autoComplete="off"
                            fullWidth
                            value={promoCode}
                            error={inputHasError(errors, 'applyPromoCode')}
                            helperText={getErrorMessage(
                                errors,
                                'applyPromoCode'
                            )}
                            onChange={(e) => {
                                setPromoCode(e.target.value.toUpperCase())
                            }}
                            FormHelperTextProps={{
                                classes: {
                                    root: classes.promoCodeInputHelperText,
                                },
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {applyPromoIsLoading ? (
                                            <GcgCircularProgress />
                                        ) : (
                                            <Button
                                                data-testid="apply-promo-code-button"
                                                className={
                                                    classes.applyPromoButton
                                                }
                                                disabled={isEmpty(promoCode)}
                                                onClick={() => {
                                                    handleApplyPromoCode(
                                                        promoCode
                                                    )
                                                }}
                                                endIcon={
                                                    <AddIcon
                                                        style={{
                                                            marginLeft: '-5px',
                                                            fontSize: '1.2em',
                                                        }}
                                                    />
                                                }
                                            >
                                                Apply
                                            </Button>
                                        )}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                </>
            )}
        </>
    )
}

export default PromoCodeInput

PromoCodeInput.prototype = {
    appliedPromoCode: PropTypes.string.isRequired,
    appliedPromoCodeTerms: PropTypes.string,
    hasQualifiedItems: PropTypes.bool,
    applyPromoIsLoading: PropTypes.bool.isRequired,
    removePromoIsLoading: PropTypes.bool.isRequired,
    errors: PropTypes.array.isRequired,
    handleApplyPromoCode: PropTypes.func.isRequired,
    handleRemovePromoCode: PropTypes.func.isRequired,
}
